import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, Code, CodeBlock, Steps, Step } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What are output mappings?', level: 2 as const },
  { id: 'configure', title: 'Configuring', level: 2 as const },
  { id: 'example', title: 'Example', level: 2 as const },
]

export function OutputMappings() {
  return (
    <Article toc={toc}>
      <H1>Output Mappings</H1>
      <Lead>Output mappings let a View write values from its result back into Input Parameters — which automatically refreshes any other View that uses those params.</Lead>

      <H2 id="what">What are output mappings?</H2>
      <P>By default, views are independent — each one fetches its own data using the plate's current input params. Output mappings break that independence: when a view receives new data, it can extract a value from that data and push it into a param, which in turn triggers a re-fetch of any view using that param.</P>
      <P>This enables patterns like:</P>
      <UL>
        <LI>Selecting a row in one view and having a detail view update to show that row's full record</LI>
        <LI>A card view showing a customer that automatically populates an orders table with that customer's orders</LI>
      </UL>
      <Callout type="info">Output mappings fire every time the view's data refreshes, not just on user interaction. Design them to be idempotent — writing the same value twice has no visible effect.</Callout>

      <H2 id="configure">Configuring</H2>
      <Steps>
        <Step n={1} title="Open the view wizard">On the plate detail, edit the view that will be the source (the one whose result drives the update).</Step>
        <Step n={2} title="Open Output Mappings">In the wizard, find the <strong>Output Mappings</strong> section.</Step>
        <Step n={3} title="Add a mapping">
          Each mapping has two fields:
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li><strong>fromPath</strong> — dot-notation path into the view's result data, e.g. <Code>0.uuid</Code> (first record's UUID)</li>
            <li><strong>toParamName</strong> — the Input Parameter name to update with that value</li>
          </ul>
        </Step>
      </Steps>
      <Callout type="tip">The <Code>fromPath</Code> uses the same dot-notation as the <Code>dataPath</Code> field. For a <Code>cup_list</Code> result, the array is at the Cup title key — e.g. <Code>Product.0.uuid</Code> is the UUID of the first product.</Callout>

      <H2 id="example">Example</H2>
      <P>A plate with two views: "Customers" (table) and "Customer Detail" (card).</P>
      <CodeBlock language="json" title="Output mapping on the Customers table view">
{`{
  "fromPath": "Customer.0.uuid",
  "toParamName": "customerId"
}`}
      </CodeBlock>
      <P>When the Customers table loads, it extracts the first customer's UUID and writes it to <Code>customerId</Code>. The Customer Detail card — which uses <Code>customerId</Code> as its input param — immediately re-fetches with the new value and shows that customer's details.</P>
      <Callout type="note">
        Pagination bar page changes bypass output mappings — the pagination bar owns the page parameter directly. Avoid setting <Code>toParamName</Code> to the same name as your pagination param to prevent conflicts.
      </Callout>
    </Article>
  )
}
